import type { CSSProperties } from 'react';
import type { LogoCarouselBlockProps, LogoItem } from '../types';
import { theme, sectionColors } from '../theme';
import { cardImage } from '../responsiveImage';
import { cellLinkKind } from './tableCellLinks';
import { photoSrc } from './Columns';

/** Space between logos, in px, for each `gap` choice. */
export const LOGO_GAPS: Record<NonNullable<LogoCarouselBlockProps['gap']>, number> = {
  small: 24,
  normal: 48,
  large: 72,
  xlarge: 104,
};

/** Seconds one logo takes to cross its own width-and-gap, for each `speed` choice. */
const SECONDS_PER_LOGO: Record<NonNullable<LogoCarouselBlockProps['speed']>, number> = {
  slow: 5,
  normal: 3,
  fast: 1.6,
};

/** The widest a row can draw: the section's content box (max-w-6xl). */
const ROW_MAX_PX = 1152;
/** Most logos one copy of a scrolling row holds, however small and few they are. */
const MAX_PER_COPY = 80;

export const LOGO_HEIGHT_DEFAULT = 48;

/** Logo height in px: blank or out of range = 48, held to 16–200. */
export function logoHeight(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  if (!Number.isFinite(n) || n <= 0) return LOGO_HEIGHT_DEFAULT;
  return Math.min(200, Math.max(16, Math.round(n)));
}

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** A saved row read safely: anything that is not a string counts as blank, so a bad row never breaks the page. */
function cleanItem(item: unknown): LogoItem | null {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const i = item as Record<string, unknown>;
  const clean = { imageUrl: text(i.imageUrl), name: text(i.name), href: text(i.href) };
  return photoSrc(clean.imageUrl) || clean.name ? clean : null;
}

/**
 * How many times one copy of the row repeats the logos, and how long one loop takes. One copy must
 * be at least as wide as the widest row (else a gap shows before the loop resets), so it counts
 * every logo as only as wide as it is tall, the narrowest a logo or a name draws.
 */
export function scrollPlan(
  count: number,
  speed: LogoCarouselBlockProps['speed'],
  height = LOGO_HEIGHT_DEFAULT,
  gap = LOGO_GAPS.normal,
): { repeat: number; seconds: number } {
  const n = Math.max(1, count);
  const repeat = Math.max(1, Math.min(Math.floor(MAX_PER_COPY / n) || 1, Math.ceil(ROW_MAX_PX / (n * (height + gap)))));
  const per = SECONDS_PER_LOGO[speed === 'slow' || speed === 'fast' ? speed : 'normal'];
  return { repeat, seconds: Math.round(count * repeat * per * 10) / 10 };
}

const JUSTIFY: Record<NonNullable<LogoCarouselBlockProps['align']>, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
  spread: 'justify-between',
};

/**
 * One logo: the picture (or the name as words), linked when it has a safe address. A copy is hidden from screen readers and the keyboard.
 * A scrolling row loads its pictures straight away (`eager`): the loop slides logos in with a CSS
 * transform, which never triggers the browser's lazy-load check, so lazy copies came into view
 * blank (seconds late in Chrome, until the visitor scrolled in Safari). A still row stays lazy.
 */
function Logo({ item, copy, eager = false }: { item: LogoItem; copy: boolean; eager?: boolean }) {
  const src = photoSrc(item.imageUrl);
  const name = item.name ?? '';
  const raw = item.href ?? '';
  const href = raw && cellLinkKind(raw) ? raw : null;
  const external = href ? /^https?:\/\//i.test(href) : false;
  const face = src ? (
    <img src={cardImage(src)} alt={copy ? '' : name} loading={eager ? 'eager' : 'lazy'} decoding="async" className="blk-logo" />
  ) : (
    <span className="blk-logo-name" style={{ fontFamily: theme.headingFont }}>{name}</span>
  );
  return (
    <li className="blk-logo-item" data-logo-dup={copy ? '' : undefined} aria-hidden={copy || undefined}>
      {href ? (
        <a
          href={href}
          tabIndex={copy ? -1 : undefined}
          aria-label={!copy && src && !name ? 'Logo link' : undefined}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {face}
        </a>
      ) : face}
    </li>
  );
}

/**
 * Logo carousel ("trust bar"): brokerage, MLS, award and press logos in one row that loops sideways,
 * or stands still and wraps. The loop is CSS only (blocks.css .blk-logos): two copies of the row
 * slide by half their width, so it never jumps. Visitors who ask for less motion get the still row;
 * the pause button stops it for good, hovering pauses it, and tabbing to a logo shows the still row. Renders nothing when no logo has a picture or a name.
 */
export function LogoCarouselBlock(props: LogoCarouselBlockProps) {
  const { heading, subheading } = props;
  const items = Array.isArray(props.items) ? props.items.map(cleanItem).filter((i): i is LogoItem => i !== null) : [];
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  if (items.length === 0) return null;

  const scroll = props.motion !== 'static';
  const beside = props.headingPlacement === 'left' && Boolean(heading || subheading);
  const gap = LOGO_GAPS[props.gap && props.gap in LOGO_GAPS ? props.gap : 'normal'];
  const height = logoHeight(props.logoHeight);
  const { repeat, seconds } = scrollPlan(items.length, props.speed, height, gap);
  const vars = {
    '--blk-logo-h': `${height}px`,
    '--blk-logo-gap': `${gap}px`,
    '--blk-logo-loop': `${seconds}s`,
  } as CSSProperties;

  const row = scroll ? (
    <div className="blk-logos-track" data-direction={props.direction === 'right' ? 'right' : 'left'}>
      {[0, 1].map((copy) => (
        // The second copy is only there to fill the loop's seam.
        <ul key={copy} className="blk-logos-group" aria-hidden={copy === 1 || undefined} data-logo-dup={copy === 1 ? '' : undefined}>
          {Array.from({ length: repeat }, (_, r) =>
            items.map((item, i) => <Logo key={`${r}-${i}`} item={item} copy={copy === 1 || r > 0} eager />),
          )}
        </ul>
      ))}
    </div>
  ) : (
    <ul className={`blk-logos-group flex-wrap ${JUSTIFY[props.align && props.align in JUSTIFY ? props.align : 'center']}`}>
      {items.map((item, i) => <Logo key={i} item={item} copy={false} />)}
    </ul>
  );

  return (
    <section className="px-6 py-10" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className={`mx-auto max-w-6xl ${beside ? 'flex flex-col gap-6 md:flex-row md:items-center md:gap-10' : ''}`}>
        {heading || subheading ? (
          <div className={beside ? 'md:w-56 md:shrink-0' : 'mb-6'}>
            {heading ? (
              <h2
                className={`${beside ? 'text-center md:text-left' : 'text-center'} text-lg font-semibold`}
                style={{ fontFamily: theme.headingFont }}
              >
                {heading}
              </h2>
            ) : null}
            {subheading ? (
              <p className={`mt-1 text-sm opacity-70 ${beside ? 'text-center md:text-left' : 'mx-auto max-w-2xl text-center'}`}>{subheading}</p>
            ) : null}
          </div>
        ) : null}
        <div className={`blk-logos-frame ${beside ? 'min-w-0 flex-1' : ''}`}>
          <div
            className="blk-logos"
            data-logo-carousel={scroll ? 'scroll' : 'static'}
            data-pause={scroll && props.pauseOnHover !== false ? '' : undefined}
            data-grayscale={props.grayscale ? '' : undefined}
            style={vars}
          >
            {row}
          </div>
          {scroll ? (
            // A pause that stays until the visitor plays it again, by mouse, touch or keyboard
            // (WCAG 2.2.2). A checkbox, so it works without script; blocks.css reads its state.
            <div className="mt-2 flex justify-end">
              <label className="blk-logos-toggle">
                <input type="checkbox" className="sr-only" aria-label="Pause the scrolling logos" />
                <span aria-hidden="true">
                  <svg className="blk-logos-pause" viewBox="0 0 16 16" width="14" height="14" fill="currentColor"><rect x="3" y="2" width="3.5" height="12" rx="1" /><rect x="9.5" y="2" width="3.5" height="12" rx="1" /></svg>
                  <svg className="blk-logos-play" viewBox="0 0 16 16" width="14" height="14" fill="currentColor"><path d="M4 2.5v11a.8.8 0 0 0 1.2.7l9-5.5a.8.8 0 0 0 0-1.4l-9-5.5A.8.8 0 0 0 4 2.5z" /></svg>
                </span>
              </label>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
